import * as Sentry from '@sentry/nextjs';
import { withFeatureFlags } from '@/flags.server';
import { dehydrate, DehydratedState } from '@tanstack/react-query';
import {
  GetServerSidePropsContext,
  GetServerSidePropsResult,
  InferGetServerSidePropsType,
} from 'next';
import { NextSeo, NextSeoProps } from 'next-seo';
import { OpenGraph } from 'next-seo/lib/types';
import { ParsedUrlQuery } from 'querystring';
import { useEffect, useMemo } from 'react';

import { logger } from '@/lib/logger';

import { TokenProfile } from '@/components/TokenProfile';

import { tokenEntityQueryKey } from '@/hooks/tokenQueries/tokenEntityCache';
// Only used in getServerSideProps; Next.js eliminates it from the client bundle.
import { fetchTokenByAddressServer } from '@/hooks/tokenQueries/tokenEntityCache.server';
import { useTokenResult } from '@/hooks/tokenQueries/useTokenResult';
import { customContentQueryKey, useCustomContent } from '@/hooks/useCustomContent';
// Only used in getServerSideProps; Next.js eliminates it from the client bundle.
import { fetchCustomContent } from '@/hooks/useCustomContent.server';

import { getBaseURL } from '@/utils/environment';
import { ChainAddress, isValidAddressForChain } from '@/utils/models';
import { makeQueryClient } from '@/utils/queryClient';

import { Header } from '../../../components/Header';
import {
  CHAIN_ID_PER_NETWORK_URL_PATH,
  NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID,
  SUPPORTED_CHAINS_BY_CHAIN_ID,
} from '../../../constants/chain';
import { trackError } from '../../../utils/errors';
import { getNativeAddressFromSymbol } from '../../../utils/multichain';
import { routes } from '../../../utils/routes';

interface TokenPageUrlParams extends ParsedUrlQuery {
  chainId: string;
  contractAddresses: string[];
}

type TokenProfilePageProps = {
  tokenAddress: string;
  chainId: number;
  dehydratedState: DehydratedState;
};

const TokenProfilePage = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const { tokenAddress, chainId } = props;

  const { token: sellToken } = useTokenResult(tokenAddress as ChainAddress, chainId);
  const { data: customContent } = useCustomContent(tokenAddress, chainId);

  // Update sentry tags on sellToken change
  useEffect(() => {
    Sentry.setTag('sell_token.address', sellToken?.address ?? 'not_set');
    Sentry.setTag('sell_token.chainId', sellToken?.chainId ?? 'not_set');
  }, [sellToken]);

  const seo: (NextSeoProps & OpenGraph) | undefined = useMemo(() => {
    if (!sellToken) {
      return undefined;
    }

    const { name, symbol, address, chainId, chainName } = sellToken;
    const chainSlug = NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID[chainId];

    const { displayName: customName } = customContent?.result?.data?.tokenContent ?? {};
    const tokenName = customName ?? name;

    const baseUrl = getBaseURL();
    const title = `Swap ${symbol} on ${chainName} | Trade at Best Price | Matcha`;
    const description = `Swap ${tokenName} (${symbol}) on ${chainName} with Matcha — the DEX aggregator that finds you the best price across 100s of liquidity sources. No hidden fees. Connect your wallet and trade in seconds.`;
    const canonical = `${baseUrl}/tokens/${chainSlug}/${address}`;

    const ogImageUrl = `${baseUrl}/api/token-og?chainId=${chainId}&address=${address}&time=${Date.now()}`;
    const images = [{ url: ogImageUrl, width: 1200, height: 630, alt: 'Matcha token cover image' }];

    return {
      title,
      description,
      canonical,
      openGraph: { title, description, images },
      additionalMetaTags: [
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: ogImageUrl },
        { name: 'twitter:url', content: canonical },
      ],
    };
  }, [sellToken, customContent]);

  return (
    <div>
      {seo && <NextSeo {...seo} />}
      <Header />
      <TokenProfile activeToken={sellToken} />
    </div>
  );
};

const validateUrlParams = (
  context: GetServerSidePropsContext<TokenPageUrlParams>,
): Pick<TokenProfilePageProps, 'tokenAddress' | 'chainId'> | undefined => {
  if (!context.params) {
    logger.error('TokenPageProps: parameters not defined.');
    return;
  }

  if (!context.params.contractAddresses.length) {
    logger.error('TokenPageProps: contractAddresses is empty.');
    return;
  }

  const chainId =
    CHAIN_ID_PER_NETWORK_URL_PATH[context.params.chainId] ?? parseInt(context.params.chainId, 10);
  if (chainId == null || Number.isNaN(chainId) || !SUPPORTED_CHAINS_BY_CHAIN_ID[chainId]) {
    logger.error(`TokenPageProps: chain id ${chainId} not valid.`);
    return;
  }

  const [addressOrSymbol] = context.params.contractAddresses;
  const tokenAddress = getNativeAddressFromSymbol(addressOrSymbol, chainId) ?? addressOrSymbol;
  if (!isValidAddressForChain(tokenAddress, chainId)) {
    logger.error(`TokenPageProps: invalid token address ${tokenAddress} for chain ${chainId}`);
    return;
  }

  return { tokenAddress, chainId };
};

export const getServerSideProps = withFeatureFlags(
  async (
    context: GetServerSidePropsContext<TokenPageUrlParams>,
  ): Promise<GetServerSidePropsResult<TokenProfilePageProps>> => {
    const queryClient = makeQueryClient();
    const notFoundRedirect = { destination: routes.ERROR_404, permanent: false };

    const urlParams = validateUrlParams(context);
    if (!urlParams) {
      return { redirect: notFoundRedirect };
    }

    try {
      const { tokenAddress, chainId } = urlParams;
      const tokenResultPromise = queryClient.fetchQuery({
        queryKey: tokenEntityQueryKey(tokenAddress, chainId),
        queryFn: () => fetchTokenByAddressServer(tokenAddress, chainId),
      });

      const tokenContentPromise = queryClient.prefetchQuery({
        queryKey: customContentQueryKey(tokenAddress, chainId),
        queryFn: () => fetchCustomContent(tokenAddress, chainId),
      });

      const [tokenResult] = await Promise.all([tokenResultPromise, tokenContentPromise]);

      if (!tokenResult) {
        logger.error(`TokenPageProps: no token found for ${tokenAddress}:${chainId}`);
        return { redirect: notFoundRedirect };
      }

      const dehydratedState = dehydrate(queryClient);
      return { props: { chainId, tokenAddress, dehydratedState } };
    } catch (error: unknown) {
      logger.error('TokenPageProps: unhandled error', error);
      trackError(error);
      return { redirect: notFoundRedirect };
    }
  },
);

export default TokenProfilePage;
